import { create } from 'zustand';
import type { ImageStateStore, ImageInformation } from './types';
import { readExpressDesignerCartStorage, writeExpressDesignerCartStorage } from './utils';

const defaultState = {
  images: [] as ImageInformation[],
  uploadBatchCompletedAt: null as number | null,
  selectedImage: undefined as ImageInformation | undefined,
  previouslyAddedProductIds: new Set<string>(),
  cartError: false
};

export const useImageStateStore = create<ImageStateStore>((set, get) => ({
  ...defaultState,

  updateImages: images => {
    set(state => {
      const indexByUuid = new Map(state.images.map((img, index) => [img.uuid, index]));
      const newImages = [...state.images];
      let changed = false;

      for (const image of images) {
        const index = image.uuid ? indexByUuid.get(image.uuid) : undefined;

        if (index === undefined) continue;

        newImages[index] = { ...newImages[index], ...image };
        changed = true;
      }

      return changed ? { images: newImages } : state;
    });
  },

  updateImage: image => {
    if (!image.uuid) return;

    set(state => {
      const index = state.images.findIndex(img => img.uuid === image.uuid);

      if (index > -1) {
        const newImages = [...state.images];

        newImages[index] = { ...newImages[index], ...image };

        return { images: newImages };
      }

      return { images: [image as ImageInformation, ...state.images] };
    });
  },

  changeUuid: ({ uploadId, clientFileId }) => {
    set(state => {
      const index = state.images.findIndex(img => img.uuid === clientFileId || img.clientFileId === clientFileId);

      if (index === -1) return state;

      const newImages = [...state.images];

      newImages[index] = { ...newImages[index], uuid: uploadId };

      return { images: newImages };
    });
  },

  removeImages: uuids => {
    set(state => ({
      images: state.images.filter(img => !uuids.includes(img.uuid))
    }));
  },

  getImages: () => get().images,

  markBatchCompleted: () => set({ uploadBatchCompletedAt: Date.now() }),

  setSelectedImage: (image: ImageInformation) => set({ selectedImage: image }),

  setCartError: (value: boolean) => set({ cartError: value }),

  setProductAdded: ({ productId, articleId, creationId }) => {
    const cart = readExpressDesignerCartStorage();

    writeExpressDesignerCartStorage({ ...cart, [productId]: { articleId, creationId } });
    set(state => ({ previouslyAddedProductIds: new Set(state.previouslyAddedProductIds).add(productId) }));
  },

  removeProductByArticleId: (articleId: number) => {
    const cart = readExpressDesignerCartStorage();
    const productId = Object.entries(cart).find(([, entry]) => entry.articleId === articleId)?.[0];

    if (!productId) return;

    const remaining = { ...cart };

    delete remaining[productId];
    writeExpressDesignerCartStorage(remaining);
    set(state => {
      const next = new Set(state.previouslyAddedProductIds);

      next.delete(productId);

      return { previouslyAddedProductIds: next };
    });
  },

  syncWithCartArticles: (cartArticleIds: number[]) =>
    set(() => {
      const cart = readExpressDesignerCartStorage();
      const activeIds = new Set(cartArticleIds);
      const matchedProductIds = Object.entries(cart)
        .filter(([, entry]) => activeIds.has(entry.articleId))
        .map(([productId]) => productId);

      return { previouslyAddedProductIds: new Set(matchedProductIds) };
    })
}));
